import omit from "lodash/omit";
import { toSelectOption } from "../../../components/Input";
import { Transaction } from "../../../services/transactions";
import { RecurringTransactionsResponse } from "../../../services/recurringTransactions/types";
import { PanelAction } from "../types";
import { clearedWhenBlank } from "../../../utils/taxWithheld";
import {
  CreateTransactionsObject,
  TRANSACTION_OPTIONS_TYPE_MAP,
  getFrequencyOption,
} from "../panels/transactions/types";

/**
 * Transform a regular transaction to create/edit object
 */
export const getTransactionObject = (
  transaction: Transaction,
): CreateTransactionsObject => {
  return {
    ...omit(
      transaction,
      "currencyCode",
      "categoryName",
      "subCategoryName",
      "toAccountName",
      "fromAccountName",
      "toAccountId",
      "fromAccountId",
      "grossAmount",
      "settlement",
      "cashbackSourceAccountId",
      "cashbackSourceAccountName",
      "cashbackExpenseId",
      "cashbackCredited",
    ),
    amount: (transaction.grossAmount ?? transaction.amount).toString(),
    taxWithheld: transaction.taxWithheld?.toString() ?? "",
    cashbackEarned: transaction.cashbackEarned?.toString() ?? null,
    isCashbackCredited: transaction.cashbackCredited,
    principalAmount: transaction.principalAmount?.toString(),
    interestAmount: transaction.interestAmount,
    transactionDate: transaction.transactionDate,
    transactionType: {
      value: TRANSACTION_OPTIONS_TYPE_MAP[transaction.transactionType],
      label: TRANSACTION_OPTIONS_TYPE_MAP[transaction.transactionType],
    },
    category: toSelectOption(transaction.categoryId, transaction.categoryName),
    subCategory: toSelectOption(
      transaction.subCategoryId,
      transaction.subCategoryName,
    ),
    toAccount: toSelectOption(
      transaction.toAccountId,
      transaction.toAccountName,
    ),
    fromAccount: toSelectOption(
      transaction.fromAccountId,
      transaction.fromAccountName,
    ),
    // Always false for regular transactions and when duplicating
    isRecurring: false,
    frequency: null,
    interval: undefined,
    stopAfterOccurrences: undefined,
  };
};

// Payload contract: no switch on screen sends no key (API keeps the stored tax); the switch off sends null (API clears it).
export const withTaxForSwitch = (
  { hasTaxWithheld, ...data }: CreateTransactionsObject,
  isTaxAvailable: boolean,
): CreateTransactionsObject => {
  if (!isTaxAvailable) {
    return { ...data, taxWithheld: undefined };
  }
  return {
    ...data,
    taxWithheld: hasTaxWithheld ? clearedWhenBlank(data.taxWithheld) : null,
  };
};

/**
 * Transform a recurring transaction to create/edit object
 */
export const getRecurringTransactionObject = (
  transaction: RecurringTransactionsResponse,
): CreateTransactionsObject => {
  return {
    id: transaction.id,
    amount: transaction.amount.toString(),
    principalAmount: undefined,
    transactionDate: transaction.nextExecutionDate,
    transactionType: {
      value: TRANSACTION_OPTIONS_TYPE_MAP[transaction.transactionType],
      label: TRANSACTION_OPTIONS_TYPE_MAP[transaction.transactionType],
    },
    category: toSelectOption(transaction.categoryId, transaction.categoryName),
    subCategory: toSelectOption(
      transaction.subCategoryId,
      transaction.subCategoryName,
    ),
    toAccount: toSelectOption(
      transaction.toAccountId,
      transaction.toAccountName,
    ),
    fromAccount: toSelectOption(
      transaction.fromAccountId,
      transaction.fromAccountName,
    ),
    note: transaction.note,
    isRecurring: true,
    frequency: getFrequencyOption(transaction.frequency),
    interval: transaction.interval,
    stopAfterOccurrences: transaction.stopAfterOccurrences,
  };
};

/**
 * Get panel title based on action
 */
export const getTitleFromAction = (action: PanelAction): string => {
  const titles: Record<PanelAction, string> = {
    create: "Create Transaction",
    edit: "Edit Transaction",
    duplicate: "Duplicate Transaction",
  };
  return titles[action];
};
